import { useState, useMemo, useCallback } from "react";
import { useDebounce } from "../../../../../hooks/useDebounce";
import LimitSelect from "../../../../../components/elements/LimitSelect";
import Search from "../../../../../components/form/Search";
import Table from "../../../../../components/elements/table/Table";
import dayjs from "dayjs";
import { lngDetect } from "../../../../../helpers/lngDetect";
import { handleFilterChange } from "../../../../../helpers";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useOutletContext } from "react-router-dom";
import * as XLSX from "xlsx";
import { transactionList } from "../../../../../services/orderService";
import { formatEventDate } from "../../../../../util/Flag";
import ExportDropdown from "../ExportDropdown";
import { reportExport } from "../../../../../services/reportService";
import { useOrganization } from "../../../../../context/OrganizationContext";
import {
  createCSVContent,
  createExcelFile,
  downloadFile,
} from "../CommonExport";
import { t } from "i18next";

// Constants moved outside component to prevent recreation
const ATTENDANCE_STATUS_STYLES = {
  confirmed: {
    backgroundColor: "#D1FAE5",
    color: "#047857",
    borderColor: "#10B981",
  },
  registered: {
    backgroundColor: "#DBEAFE",
    color: "#1D4ED8",
    borderColor: "#3B82F6",
  },
  "no-show": {
    backgroundColor: "#FEE2E2",
    color: "#DC2626",
    borderColor: "#EF4444",
  },
  processing: {
    backgroundColor: "#FEF3C7",
    color: "#D97706",
    borderColor: "#F59E0B",
  },
};

const TICKET_TYPE_STYLES = {
  "addons-ticket": {
    backgroundColor: "#FDF4FF",
    color: "#7C2D92",
    borderColor: "#A855F7",
  },
  "addons-event": {
    backgroundColor: "#FEF3C7",
    color: "#D97706",
    borderColor: "#F59E0B",
  },
  ticket: {
    backgroundColor: "#DBEAFE",
    color: "#1D4ED8",
    borderColor: "#3B82F6",
  },
  variation: {
    backgroundColor: "#ECFDF5",
    color: "#065F46",
    borderColor: "#059669",
  },
};

const DEFAULT_STYLE = {
  backgroundColor: "#F3F4F6",
  color: "#374151",
  borderColor: "#6B7280",
};

const getAttendanceStatusStyle = (status) => {
  return ATTENDANCE_STATUS_STYLES[status?.toLowerCase()] || DEFAULT_STYLE;
};

const getTicketTypeStyle = (ticketType) => {
  return TICKET_TYPE_STYLES[ticketType?.toLowerCase()] || DEFAULT_STYLE;
};

const AttendeeList = () => {
  const { event } = useOutletContext();
  const { selectedOrganization } = useOrganization();
  const [selectedItems, setSelectedItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  // Memoized query key
  const queryKey = useMemo(
    () => ["event-attendee", filters, search, event?.data?._id],
    [filters, search, event?.data?._id]
  );

  // Fetch attendees data
  const { data, isLoading, isFetching } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await transactionList({
        ...filters,
        ...(search && { keyword: search }),
        orderStatus: ["confirmed"],
        event: event?.data?._id,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!event?.data?._id,
  });

  // Send file to API
  const sendFileToAPI = useCallback(
    async ({ fileContent, localDownloadStatus, fileName, format, event }) => {
      try {
        const formData = new FormData();
        formData.append("event", event);
        formData.append("file", fileContent, fileName);
        formData.append("type", "attendees");
        formData.append("name", `Attendees Report (${format?.toUpperCase()})`);
        formData.append("organization", selectedOrganization);
        formData.append("status", localDownloadStatus ? "success" : "failed");

        const res = await reportExport(formData);
        return res?.status === 200 || res?.status === 201;
      } catch (error) {
        return false;
      }
    },
    [selectedOrganization]
  );

  // Unified export handler
  const handleExport = useCallback(
    async (format) => {
      setIsExportOpen(false);
      toast.info("Preparing export data...");

      try {
        const exportResponse = await transactionList({
          ...filters,
          ...(search && { keyword: search }),
          limit: data?.metadata?.totalDocs,
          page: 1,
          event: event?.data?._id,
          orderStatus: ["confirmed"],
        });

        if (exportResponse?.docs?.status !== 200) {
          toast.error(exportResponse?.message || "Failed to fetch export data");
          return;
        }

        const attendeeData = exportResponse?.docs?.data || [];
        if (!attendeeData || attendeeData.length === 0) {
          toast.warning("No data available to export");
          return;
        }

        // Transform data
        const exportData = attendeeData.map((attendee) => {
          const attendee_name = attendee?.attendeeData
            ?.filter((attendee) =>
              ["First Name", "Last Name"].includes(attendee?.question)
            )
            ?.map((attendee) => `${attendee?.answers || ""}`.trim())
            .join(" ");
          const attendee_email = attendee?.attendeeData
            ?.filter((attendee) => ["Email"].includes(attendee?.question))
            ?.map((attendee) => `${attendee?.answers || ""}`.trim())
            .join(" ");
          return {
            "Attendee Name": attendee_name || "-",
            Email: attendee_email || "-",
            "Event Name":
              lngDetect(attendee?.orderData?.eventData?.title) || "-",
            "Ticket Name": lngDetect(attendee?.ticketInfo?.name) || "-",
            "Ticket Type":
              attendee?.ticketInfo?.type
                .split("-")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ") || "standard",
            "Registration Date": attendee?.createdAt
              ? dayjs(attendee.createdAt).format("MM-DD-YYYY")
              : "-",
            "Attendance Status":
              attendee?.orderData?.status?.info.charAt(0)?.toUpperCase() +
                attendee?.orderData?.status?.info.slice(1) || "registered",
          };
        });

        const filename = `attendees_export_${dayjs().format("YYYY-MM-DD")}`;

        let blob, fullFilename;
        if (format === "csv") {
          const csvContent = createCSVContent(exportData);
          blob = new Blob([csvContent], { type: "text/csv" });
          fullFilename = `${filename}.csv`;
        } else {
          const workbook = createExcelFile(exportData);
          if (format === "xlsx") {
            const excelBuffer = XLSX.write(workbook, {
              bookType: "xlsx",
              type: "array",
            });
            blob = new Blob([excelBuffer], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            fullFilename = `${filename}.xlsx`;
          }
        }

        const downloadSuccess = await downloadFile(blob, fullFilename);

        if (downloadSuccess) {
          toast.success(
            `${format.toUpperCase()} file ${
              window.showSaveFilePicker ? "saved" : "downloaded"
            } successfully!`
          );

          try {
            await sendFileToAPI({
              fileName: fullFilename,
              fileContent: blob,
              localDownloadStatus: downloadSuccess,
              format: format,
              event: event?.data?._id,
            });
          } catch (apiError) {
            console.error("API upload failed:", apiError);
            if (window.showSaveFilePicker) {
              toast.warning("File saved locally but failed to sync to server");
            }
          }
        }
      } catch (error) {
        toast.error("Export failed. Please try again.");
      }
    },
    [
      filters,
      search,
      data?.metadata?.totalDocs,
      event?.data?._id,
      createCSVContent,
      createExcelFile,
      downloadFile,
      sendFileToAPI,
    ]
  );

  // Memoized table head data
  const tableHeadData = [
    {
      _id: "attendeeName",
      name: t("Attendee Name"),
      align: "left",
    },
    {
      _id: 1,
      name: t("Email"),
      align: "left",
    },
    {
      _id: 2,
      name: t("Event Name"),
      align: "left",
    },
    {
      _id: 3,
      name: t("Ticket Name"),
      align: "left",
    },
    {
      _id: "ticketType",
      name: t("Ticket Type"),
      align: "left",
    },
    {
      _id: 4,
      name: t("Registration Date"),
      align: "left",
    },
    {
      _id: "attendanceStatus",
      name: t("Attendance Status"),
      align: "left",
    },
  ];

  // Memoized table data with optimized rendering
  const tableData = useMemo(() => {
    return (
      data?.data?.map((item) => {
        const attendanceStatus = item?.orderData?.status?.info || "registered";
        const attendanceStatusText =
          attendanceStatus.charAt(0)?.toUpperCase() + attendanceStatus.slice(1);
        const attendanceStatusStyle =
          getAttendanceStatusStyle(attendanceStatus);

        const ticketType = item?.ticketInfo?.type || "standard";
        const ticketTypeText = ticketType
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        const ticketTypeStyle = getTicketTypeStyle(ticketType);
        const attendee_name = item?.attendeeData
          ?.filter((attendee) =>
            ["First Name", "Last Name"].includes(attendee?.question)
          )
          ?.map((attendee) => `${attendee?.answers || ""}`.trim())
          .join(" ");
        const attendee_email = item?.attendeeData
          ?.filter((attendee) => ["Email"].includes(attendee?.question))
          ?.map((attendee) => `${attendee?.answers || ""}`.trim())
          .join(" ");

        return {
          _id: item?._id,
          rowData: [
            {
              _id: 1,
              type: "text",
              align: "left",
              cellClass: "!min-w-[150px] text-purple-600 font-medium",
              data: attendee_name || "-",
            },
            {
              _id: 2,
              type: "text",
              align: "left",
              cellClass: "!min-w-[200px] text-gray-600",
              data: attendee_email || "-",
            },
            {
              _id: 3,
              type: "text",
              align: "left",
              cellClass: "!min-w-[150px] text-purple-600",
              data: lngDetect(item?.orderData?.eventData?.title) || "-",
            },
            {
              _id: 4,
              type: "text",
              align: "left",
              cellClass: "!min-w-[150px] text-purple-600",
              data: lngDetect(item?.ticketInfo?.name) || "-",
            },
            {
              _id: "ticketType",
              type: "text",
              align: "left",
              cellClass: "!min-w-[120px]",
              data: (
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
                  style={ticketTypeStyle}
                >
                  {ticketTypeText}
                </span>
              ),
            },
            {
              _id: 5,
              type: "text",
              align: "left",
              cellClass: "!min-w-[130px] text-gray-600",
              data: dayjs(item?.createdAt).format("MMM DD, YYYY") || "-",
            },
            {
              _id: "attendanceStatus",
              type: "text",
              align: "left",
              cellClass: "!min-w-[120px]",
              data: (
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
                  style={attendanceStatusStyle}
                >
                  {attendanceStatusText}
                </span>
              ),
            },
          ],
        };
      }) || []
    );
  }, [data?.data]);

  // Memoized callbacks
  const handleLimitChange = useCallback((val) => {
    handleFilterChange("limit", val, setFilters);
  }, []);

  const handleSearch = useCallback((val) => {
    setKeyword(val);
    handleFilterChange("page", 1, setFilters);
  }, []);

  const handlePageChange = useCallback((val) => {
    handleFilterChange("page", val, setFilters);
    setSelectedItems([]);
  }, []);

  const toggleExportDropdown = useCallback(() => {
    setIsExportOpen(!isExportOpen);
  }, [isExportOpen]);

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={handleLimitChange}
            />
          </div>
          <div className="w-72 mt-3">
            <Search placeholder={t("Search here")} search={handleSearch} />
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <ExportDropdown
            onExport={handleExport}
            isOpen={isExportOpen}
            onToggle={toggleExportDropdown}
            isLoading={isExporting}
          />
        </div>
      </div>

      {/* Attendees Table */}
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={handlePageChange}
        originalData={[...(data?.data || [])]}
        setOriginalData={(val) => {
          queryClient.setQueryData(queryKey, (state) => {
            if (state) {
              return { ...state, data: val };
            }
          });
        }}
        isFetching={isFetching}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
      />
    </div>
  );
};

export default AttendeeList;
